// Linux command lessons - base set (20 lessons)
const baseLinuxLessons = [
  {
    title: "Finding Text with grep",
    description: "The grep command searches for patterns in files or command output.",
    task: "Write a command to search for the word 'error' in a file called 'logfile.txt'.",
    hint: "The basic syntax is grep PATTERN FILENAME.",
    example: "grep hello file.txt",
    answer: "grep error logfile.txt",
    tips: [
      "grep is case-sensitive by default",
      "Add -i for case-insensitive search"
    ],
    commandRef: {
      command: "grep",
      description: "Search for patterns in files or input - one of the most used Linux commands",
      syntax: "grep [OPTIONS] PATTERN [FILE...]",
      options: [
        { flag: "-i", description: "Case-insensitive search" },
        { flag: "-r", description: "Search recursively in directories" },
        { flag: "-n", description: "Show line numbers" },
        { flag: "-v", description: "Invert match (show non-matching lines)" }
      ],
      examples: [
        { code: "grep error log.txt", description: "Find 'error' in file" },
        { code: "grep -i 'warning' *.log", description: "Case-insensitive search in all .log files" },
        { code: "grep -rn 'TODO' src/", description: "Recursive search with line numbers" }
      ]
    }
  },
  {
    title: "Filtering Output with grep",
    description: "You can combine commands using pipes (|) to filter the output of one command with another.",
    task: "Write a command to list all processes (ps aux) and filter for 'firefox' processes.",
    hint: "Use the pipe character to send the output of ps aux to grep.",
    example: "command1 | grep pattern",
    answer: ["ps aux | grep firefox", "ps -aux | grep firefox", "ps aux|grep firefox"],
    tips: [
      "The pipe symbol (|) connects the output of one command to the input of another",
      "This is a common pattern for filtering large outputs"
    ],
    commandRef: {
      command: "grep (with pipes)",
      description: "Filter command output by piping through grep",
      syntax: "command | grep PATTERN",
      options: [
        { flag: "|", description: "Pipe - sends output of left command to input of right" },
        { flag: "-v", description: "Exclude matching lines" },
        { flag: "-c", description: "Count matching lines only" }
      ],
      examples: [
        { code: "ps aux | grep nginx", description: "Find nginx processes" },
        { code: "cat log.txt | grep -v DEBUG", description: "Filter out DEBUG lines" },
        { code: "history | grep ssh", description: "Find SSH commands in history" }
      ]
    }
  },
  {
    title: "Text Processing with awk",
    description: "awk is a powerful text processing tool that works on a line-by-line basis.",
    task: "Write an awk command to print the first column from a file called 'data.txt'.",
    hint: "awk uses $1, $2, etc. to refer to columns in a text file.",
    example: "awk '{print $2}' file.txt",
    answer: ["awk '{print $1}' data.txt", "awk '{print $1}' data.txt"],
    tips: [
      "By default, awk splits on any whitespace (spaces or tabs)",
      "The command must be enclosed in quotes"
    ],
    commandRef: {
      command: "awk",
      description: "Pattern scanning and processing language for text manipulation",
      syntax: "awk [OPTIONS] 'PATTERN {ACTION}' [FILE...]",
      options: [
        { flag: "$1, $2...", description: "Column/field references" },
        { flag: "$0", description: "The entire line" },
        { flag: "-F", description: "Set field separator" },
        { flag: "NR", description: "Current line number" }
      ],
      examples: [
        { code: "awk '{print $1}' file.txt", description: "Print first column" },
        { code: "awk -F: '{print $1}' /etc/passwd", description: "Print usernames" },
        { code: "awk 'NR>1' file.csv", description: "Skip header row" }
      ]
    }
  },
  {
    title: "Text Substitution with sed",
    description: "The sed command allows you to perform text substitutions in files or command output.",
    task: "Write a sed command to replace all occurrences of 'old' with 'new' in a file called 'text.txt'.",
    hint: "The s/// syntax is used for substitution in sed.",
    example: "sed 's/pattern/replacement/' file.txt",
    answer: ["sed 's/old/new/g' text.txt", "sed 's/old/new/g' text.txt"],
    tips: [
      "The g flag (global) ensures all occurrences in each line are replaced",
      "Without g, only the first occurrence on each line is replaced"
    ],
    commandRef: {
      command: "sed",
      description: "Stream editor for filtering and transforming text",
      syntax: "sed [OPTIONS] 'COMMAND' [FILE...]",
      options: [
        { flag: "s/old/new/", description: "Substitute old with new" },
        { flag: "g", description: "Global - replace all occurrences per line" },
        { flag: "-i", description: "Edit file in-place" },
        { flag: "-n", description: "Suppress automatic output" }
      ],
      examples: [
        { code: "sed 's/foo/bar/g' file.txt", description: "Replace all foo with bar" },
        { code: "sed -i 's/old/new/g' file.txt", description: "Edit file in-place" },
        { code: "sed '5d' file.txt", description: "Delete line 5" }
      ]
    }
  },
  {
    title: "Viewing Process Information",
    description: "The ps command shows information about running processes.",
    task: "Write the command to show all processes with detailed information.",
    hint: "You need a specific combination of options to see all processes in a detailed format.",
    example: "ps with options",
    answer: ["ps aux", "ps -aux", "ps -ef"],
    tips: [
      "ps aux shows processes for all users in a BSD-style format",
      "ps -ef shows a similar output in a Unix-style format"
    ],
    commandRef: {
      command: "ps",
      description: "Report a snapshot of current processes",
      syntax: "ps [OPTIONS]",
      options: [
        { flag: "aux", description: "All users, user-oriented format, include processes without tty" },
        { flag: "-ef", description: "Full-format listing of all processes" },
        { flag: "-p PID", description: "Select by process ID" },
        { flag: "--forest", description: "Show process tree" }
      ],
      examples: [
        { code: "ps aux", description: "List all processes (BSD style)" },
        { code: "ps -ef", description: "List all processes (Unix style)" },
        { code: "ps aux --forest", description: "Show process tree" }
      ]
    }
  },
  {
    title: "Process Control",
    description: "The kill command sends signals to processes, typically to stop them.",
    task: "Write the command to terminate a process with PID 1234.",
    hint: "The basic syntax is kill followed by the process ID.",
    example: "kill 5678",
    answer: ["kill 1234", "kill -9 1234", "kill -SIGTERM 1234", "kill -SIGKILL 1234"],
    tips: [
      "The default signal is SIGTERM, which requests termination",
      "SIGKILL (-9) forces termination but doesn't allow cleanup"
    ],
    commandRef: {
      command: "kill",
      description: "Send a signal to a process, typically to terminate it",
      syntax: "kill [OPTIONS] PID...",
      options: [
        { flag: "-9", description: "SIGKILL - force kill (cannot be caught)" },
        { flag: "-15", description: "SIGTERM - graceful termination (default)" },
        { flag: "-l", description: "List all signal names" },
        { flag: "-HUP", description: "SIGHUP - hangup/reload configuration" }
      ],
      examples: [
        { code: "kill 1234", description: "Gracefully terminate process 1234" },
        { code: "kill -9 1234", description: "Force kill process 1234" },
        { code: "killall nginx", description: "Kill all processes named nginx" }
      ]
    }
  },
  {
    title: "Finding Files with find",
    description: "The find command searches for files in a directory hierarchy based on various criteria.",
    task: "Write a command to find all .jpg files in the /home/user directory.",
    hint: "Use the -name option to search by filename pattern.",
    example: "find /path -name '*.txt'",
    answer: ["find /home/user -name '*.jpg'", "find /home/user -name \"*.jpg\""],
    tips: [
      "Use quotes around the pattern to prevent shell expansion",
      "The search is recursive by default"
    ],
    commandRef: {
      command: "find",
      description: "Search for files in a directory hierarchy",
      syntax: "find [PATH] [OPTIONS] [EXPRESSION]",
      options: [
        { flag: "-name PATTERN", description: "Search by filename (case-sensitive)" },
        { flag: "-iname PATTERN", description: "Search by filename (case-insensitive)" },
        { flag: "-type f", description: "Find only files" },
        { flag: "-type d", description: "Find only directories" },
        { flag: "-mtime -N", description: "Modified in last N days" }
      ],
      examples: [
        { code: "find . -name '*.txt'", description: "Find all .txt files" },
        { code: "find /var -type f -size +100M", description: "Files larger than 100MB" },
        { code: "find . -mtime -1", description: "Modified in last 24 hours" }
      ]
    }
  },
  {
    title: "Filtering with find",
    description: "The find command can filter files based on various criteria like size or modification time.",
    task: "Write a command to find files larger than 10MB in the current directory.",
    hint: "Use the -size option with appropriate units.",
    example: "find . -size +5M",
    answer: ["find . -size +10M", "find . -size +10MB", "find . -size +10240k"],
    tips: [
      "The + prefix means 'greater than'",
      "Size units: c (bytes), k (kilobytes), M (megabytes), G (gigabytes)"
    ],
    commandRef: {
      command: "find",
      description: "Search for files with size and time filters",
      syntax: "find [PATH] -size [+/-]N[unit]",
      options: [
        { flag: "-size +10M", description: "Files larger than 10 megabytes" },
        { flag: "-size -1k", description: "Files smaller than 1 kilobyte" },
        { flag: "-mtime -7", description: "Modified in last 7 days" },
        { flag: "-newer file", description: "Modified more recently than file" }
      ],
      examples: [
        { code: "find . -size +100M", description: "Find files over 100MB" },
        { code: "find /tmp -size 0", description: "Find empty files" },
        { code: "find . -mmin -60", description: "Modified in last 60 minutes" }
      ]
    }
  },
  {
    title: "File Compression with tar",
    description: "The tar command archives multiple files into a single file, often with compression.",
    task: "Write a command to create a compressed tar archive named 'backup.tar.gz' containing a directory named 'documents'.",
    hint: "Use tar with options for compression and archive creation.",
    example: "tar -czvf archive.tar.gz directory",
    answer: ["tar -czvf backup.tar.gz documents", "tar -czf backup.tar.gz documents"],
    tips: [
      "Options: c (create), z (gzip), v (verbose), f (file name)",
      "The archive name must come immediately after the f option"
    ],
    commandRef: {
      command: "tar",
      description: "Archive multiple files into a single file, with optional compression",
      syntax: "tar [OPTIONS] ARCHIVE [FILE...]",
      options: [
        { flag: "-c", description: "Create a new archive" },
        { flag: "-x", description: "Extract files from archive" },
        { flag: "-z", description: "Compress/decompress with gzip" },
        { flag: "-v", description: "Verbose - list files processed" },
        { flag: "-f FILE", description: "Use archive file" }
      ],
      examples: [
        { code: "tar -czvf backup.tar.gz dir/", description: "Create compressed archive" },
        { code: "tar -xzvf backup.tar.gz", description: "Extract compressed archive" },
        { code: "tar -tf archive.tar", description: "List archive contents" }
      ]
    }
  },
  {
    title: "Extracting Archives",
    description: "The tar command can also extract files from an archive.",
    task: "Write a command to extract files from 'archive.tar.gz'.",
    hint: "Use tar with options for extraction.",
    example: "tar -xvf archive.tar",
    answer: ["tar -xzvf archive.tar.gz", "tar -xzf archive.tar.gz", "tar -xf archive.tar.gz"],
    tips: [
      "Options: x (extract), z (gzip), v (verbose), f (file name)",
      "The z option is needed for .gz files but modern tar can detect automatically"
    ],
    commandRef: {
      command: "tar",
      description: "Extract files from a tar archive",
      syntax: "tar -x [OPTIONS] -f ARCHIVE",
      options: [
        { flag: "-x", description: "Extract mode" },
        { flag: "-z", description: "Handle gzip compression" },
        { flag: "-j", description: "Handle bzip2 compression" },
        { flag: "-C DIR", description: "Extract to specific directory" }
      ],
      examples: [
        { code: "tar -xzvf file.tar.gz", description: "Extract gzip archive" },
        { code: "tar -xjvf file.tar.bz2", description: "Extract bzip2 archive" },
        { code: "tar -xf file.tar -C /tmp", description: "Extract to /tmp" }
      ]
    }
  },
  {
    title: "Disk Usage",
    description: "The du command shows disk usage of files and directories.",
    task: "Write a command to show the disk usage of a directory named 'projects' in a human-readable format.",
    hint: "Use du with an option for human-readable sizes.",
    example: "du -h directory",
    answer: ["du -h projects", "du -sh projects", "du -hs projects"],
    tips: [
      "The -h option shows sizes in KB, MB, GB",
      "The -s option shows only a summary for each directory"
    ],
    commandRef: {
      command: "du",
      description: "Estimate file and directory disk usage",
      syntax: "du [OPTIONS] [FILE...]",
      options: [
        { flag: "-h", description: "Human-readable sizes (KB, MB, GB)" },
        { flag: "-s", description: "Summary only (total for each argument)" },
        { flag: "-a", description: "Show all files, not just directories" },
        { flag: "--max-depth=N", description: "Limit directory depth" }
      ],
      examples: [
        { code: "du -sh *", description: "Size of each item in current dir" },
        { code: "du -h --max-depth=1", description: "One level deep with sizes" },
        { code: "du -a | sort -rh | head", description: "Find largest files" }
      ]
    }
  },
  {
    title: "Free Disk Space",
    description: "The df command shows free disk space on all mounted filesystems.",
    task: "Write a command to show free disk space in a human-readable format.",
    hint: "Use df with an option for human-readable sizes.",
    example: "df options",
    answer: ["df -h", "df --human-readable"],
    tips: [
      "The -h option shows sizes in KB, MB, GB",
      "This shows all mounted filesystems by default"
    ],
    commandRef: {
      command: "df",
      description: "Report filesystem disk space usage",
      syntax: "df [OPTIONS] [FILESYSTEM...]",
      options: [
        { flag: "-h", description: "Human-readable sizes" },
        { flag: "-T", description: "Show filesystem type" },
        { flag: "-i", description: "Show inode information" },
        { flag: "--total", description: "Show grand total" }
      ],
      examples: [
        { code: "df -h", description: "Show all filesystems human-readable" },
        { code: "df -hT", description: "Include filesystem types" },
        { code: "df -h /home", description: "Show space for /home" }
      ]
    }
  },
  {
    title: "Sorting Text",
    description: "The sort command sorts lines of text files.",
    task: "Write a command to sort the contents of a file named 'names.txt' alphabetically.",
    hint: "The basic syntax is sort followed by the filename.",
    example: "sort file.txt",
    answer: "sort names.txt",
    tips: [
      "Use -r for reverse sort",
      "Use -n for numeric sort"
    ],
    commandRef: {
      command: "sort",
      description: "Sort lines of text files",
      syntax: "sort [OPTIONS] [FILE...]",
      options: [
        { flag: "-r", description: "Reverse the sort order" },
        { flag: "-n", description: "Numeric sort (10 comes after 2)" },
        { flag: "-k N", description: "Sort by column N" },
        { flag: "-u", description: "Unique - remove duplicate lines" }
      ],
      examples: [
        { code: "sort file.txt", description: "Alphabetical sort" },
        { code: "sort -rn numbers.txt", description: "Reverse numeric sort" },
        { code: "sort -t: -k3 -n /etc/passwd", description: "Sort by UID column" }
      ]
    }
  },
  {
    title: "Removing Duplicates",
    description: "The uniq command filters out duplicate lines from sorted input.",
    task: "Write a command to display only unique lines from a sorted file named 'data.txt'.",
    hint: "The file must be sorted first for uniq to work properly.",
    example: "uniq sorted_file.txt",
    answer: ["uniq data.txt", "sort data.txt | uniq"],
    tips: [
      "uniq only removes adjacent duplicate lines, so sorting first is important",
      "Use -c to count occurrences of each line"
    ],
    commandRef: {
      command: "uniq",
      description: "Report or filter out repeated lines (requires sorted input)",
      syntax: "uniq [OPTIONS] [INPUT [OUTPUT]]",
      options: [
        { flag: "-c", description: "Prefix lines with occurrence count" },
        { flag: "-d", description: "Only print duplicate lines" },
        { flag: "-u", description: "Only print unique lines" },
        { flag: "-i", description: "Ignore case when comparing" }
      ],
      examples: [
        { code: "sort file.txt | uniq", description: "Remove duplicates" },
        { code: "sort file.txt | uniq -c", description: "Count occurrences" },
        { code: "sort file.txt | uniq -d", description: "Show only duplicates" }
      ]
    }
  },
  {
    title: "Network Connections",
    description: "The netstat command displays network connections, routing tables, and interface statistics.",
    task: "Write a command to show all listening TCP ports.",
    hint: "Use netstat with options to show listening ports and TCP connections.",
    example: "netstat options",
    answer: ["netstat -tln", "netstat -lt", "netstat -tlpn", "ss -tln"],
    tips: [
      "Options: t (TCP), l (listening), n (numeric), p (programs)",
      "The newer ss command can be used instead of netstat"
    ],
    commandRef: {
      command: "netstat / ss",
      description: "Display network connections and listening ports",
      syntax: "netstat [OPTIONS] or ss [OPTIONS]",
      options: [
        { flag: "-t", description: "Show TCP connections" },
        { flag: "-u", description: "Show UDP connections" },
        { flag: "-l", description: "Show only listening sockets" },
        { flag: "-n", description: "Show numeric addresses" },
        { flag: "-p", description: "Show process using socket" }
      ],
      examples: [
        { code: "netstat -tuln", description: "All listening TCP/UDP ports" },
        { code: "ss -tuln", description: "Same with modern ss command" },
        { code: "netstat -tulnp", description: "Include process info (sudo)" }
      ]
    }
  },
  {
    title: "Background Processes",
    description: "You can run commands in the background by appending an ampersand (&).",
    task: "Write a command to run 'long_script.sh' in the background.",
    hint: "Add a special character at the end of the command.",
    example: "command &",
    answer: ["./long_script.sh &", "bash long_script.sh &", "sh long_script.sh &"],
    tips: [
      "The command will run in the background and you'll get your prompt back",
      "The terminal will show the job number and process ID"
    ],
    commandRef: {
      command: "& (background)",
      description: "Run a command in the background",
      syntax: "command &",
      options: [
        { flag: "&", description: "Run command in background" },
        { flag: "nohup", description: "Prevent hangup signal when terminal closes" },
        { flag: "disown", description: "Remove job from shell's job table" }
      ],
      examples: [
        { code: "./script.sh &", description: "Run script in background" },
        { code: "nohup ./script.sh &", description: "Run and survive logout" },
        { code: "command > log.txt 2>&1 &", description: "Background with logging" }
      ]
    }
  },
  {
    title: "Viewing Background Jobs",
    description: "The jobs command shows the status of jobs running in the background.",
    task: "Write the command to list all background jobs.",
    hint: "This command has no required arguments.",
    example: "j___",
    answer: "jobs",
    tips: [
      "Each job has a job number that can be referenced with %",
      "Use fg %n to bring job number n to the foreground"
    ],
    commandRef: {
      command: "jobs",
      description: "List background jobs in current shell session",
      syntax: "jobs [OPTIONS]",
      options: [
        { flag: "-l", description: "List process IDs as well" },
        { flag: "-p", description: "List process IDs only" },
        { flag: "%N", description: "Reference job number N" }
      ],
      examples: [
        { code: "jobs", description: "List all background jobs" },
        { code: "fg %1", description: "Bring job 1 to foreground" },
        { code: "bg %2", description: "Resume job 2 in background" }
      ]
    }
  },
  {
    title: "System Information",
    description: "The uname command displays basic system information.",
    task: "Write the command to show all system information including kernel version, hostname, and architecture.",
    hint: "Use uname with an option to show all information.",
    example: "uname -option",
    answer: ["uname -a", "uname --all"],
    tips: [
      "This shows kernel name, hostname, kernel version, and hardware architecture",
      "Use individual options like -s, -n, -r for specific information"
    ],
    commandRef: {
      command: "uname",
      description: "Print system information",
      syntax: "uname [OPTIONS]",
      options: [
        { flag: "-a", description: "All information" },
        { flag: "-s", description: "Kernel name" },
        { flag: "-r", description: "Kernel release" },
        { flag: "-m", description: "Machine hardware name" },
        { flag: "-n", description: "Network node hostname" }
      ],
      examples: [
        { code: "uname -a", description: "Show all system info" },
        { code: "uname -r", description: "Show kernel version" },
        { code: "uname -m", description: "Show architecture (x86_64)" }
      ]
    }
  },
  {
    title: "CPU Information",
    description: "Information about your CPU can be found in the /proc/cpuinfo file.",
    task: "Write a command to display the contents of the CPU information file.",
    hint: "Use cat to view the contents of this special file.",
    example: "cat /proc/file",
    answer: "cat /proc/cpuinfo",
    tips: [
      "This file contains detailed information about each CPU core",
      "Use grep to filter for specific information: cat /proc/cpuinfo | grep 'model name'"
    ],
    commandRef: {
      command: "/proc filesystem",
      description: "Virtual filesystem providing process and system information",
      syntax: "cat /proc/[file]",
      options: [
        { flag: "/proc/cpuinfo", description: "CPU information" },
        { flag: "/proc/meminfo", description: "Memory information" },
        { flag: "/proc/version", description: "Kernel version" },
        { flag: "/proc/loadavg", description: "System load averages" }
      ],
      examples: [
        { code: "cat /proc/cpuinfo", description: "Show CPU details" },
        { code: "cat /proc/meminfo", description: "Show memory details" },
        { code: "cat /proc/cpuinfo | grep 'model name'", description: "CPU model only" }
      ]
    }
  },
  {
    title: "Memory Information",
    description: "The free command displays information about system memory usage.",
    task: "Write a command to show memory usage in a human-readable format.",
    hint: "Use free with an option for human-readable sizes.",
    example: "free options",
    answer: ["free -h", "free --human"],
    tips: [
      "This shows total, used, and free memory for both RAM and swap",
      "The -m option shows sizes in megabytes, -g in gigabytes"
    ],
    commandRef: {
      command: "free",
      description: "Display amount of free and used memory in the system",
      syntax: "free [OPTIONS]",
      options: [
        { flag: "-h", description: "Human-readable output" },
        { flag: "-m", description: "Show output in megabytes" },
        { flag: "-g", description: "Show output in gigabytes" },
        { flag: "-s N", description: "Repeat every N seconds" }
      ],
      examples: [
        { code: "free -h", description: "Human-readable memory info" },
        { code: "free -m", description: "Memory in megabytes" },
        { code: "free -h -s 5", description: "Update every 5 seconds" }
      ]
    }
  }
];

// Expand the base lessons to ~100 items by repeating them
const linuxLessons = [];
for (let i = 0; i < 100; i++) {
  linuxLessons.push({ ...baseLinuxLessons[i % baseLinuxLessons.length] });
}

export default linuxLessons;
